import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { kpiService } from "./kpi-service";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import { ReactComponent as IconSeveridad } from "../../../assets/img/icon/dashboard/severidad.svg";
import { queryChartType, QueryChartTypeKey } from "../schemas/kpi-props.schema";
import { ReactComponent as IconMenu } from "../../../assets/img/icon/dashboard/dashboard_button_modal.svg";
import { ProdesegButton, ProdesegButtonEnumTypes } from "../../../components/ProdesegButton/ProdesegButton";
import { StatsModal } from "./StatsModal";
import { ChartData, ChartOptions } from "chart.js";
import { formatCurrency } from "../schemas/dashboard.utils";
import { useBoolean } from "../../../custom-hooks/useBoolean";
import { CubeBasicFilters, CubeSummaryResponse } from "../schemas/kpi.types";

export const KPIDoughnutSummary: React.FC<{
    cubeFilters: CubeBasicFilters;
    typeInquiry: QueryChartTypeKey;
    titleChart: string;
    titleFlag: boolean;
}> = ({ cubeFilters, typeInquiry, titleChart, titleFlag = false }) => {
    const [chartData, setChartData] = useState<ChartData<"doughnut">>({
        labels: [],
        datasets: [],
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [rawStats, setRawStats] = useState<CubeSummaryResponse[]>([]);
    const { value: isModalOpen, toggle: toggleModal } = useBoolean(false);
    const { idQuotes, idClient, policy } = cubeFilters;

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const doughnutCubeData = await kpiService.fetchTypeOfCare(cubeFilters, typeInquiry);

                setRawStats(doughnutCubeData);
                setChartData({
                    labels: doughnutCubeData.map(d => d.type),
                    datasets: [
                        {
                            label: "Siniestros",
                            data: doughnutCubeData.map(d => d.amount),
                            backgroundColor: ["#0D8A8A", "#81CED2", "#79CECF", "#D2EFEF"],
                            borderWidth: 0,
                            hoverOffset: 4,
                        },
                    ],
                });
            } catch (error) {
                console.error("Error al cargar el gráfico:", error);
            } finally {
                setLoading(false);
            }
        };

        if (idClient) void loadData();
    }, [idClient, idQuotes, policy, typeInquiry]);

    const options: ChartOptions<"doughnut"> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 10,
                bottom: 10,
                left: 40,
                right: 40,
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
            datalabels: {
                display: true,
                anchor: "end",
                align: "end",
                offset: 5,
                formatter: (value, context) => {
                    const dataset = context.chart.data.datasets[0];
                    const total = (dataset.data as number[]).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return `${percentage} %`;
                },
                color: "#666",
                font: { weight: "bold", size: 12 },
            },
        },
        cutout: "80%",
    };

    const dataValues = chartData.datasets[0]?.data || [];
    const total = dataValues.reduce((a, b) => a + b, 0) || 0;

    const renderContent = () => (
        <>
            {loading ? (
                <div className="text-center py-4">
                    <Spinner color="primary" />
                </div>
            ) : (
                <CardBody className={"py-4 px-3 d-flex flex-column h-100"}>
                    {titleFlag && (
                        <div className={"d-flex justify-content-between align-items-center mb-4"}>
                            <h2 className={"title-label mb-0"}>{titleChart}</h2>
                            <ProdesegButton
                                type={ProdesegButtonEnumTypes.PrimaryLight}
                                Icon={IconMenu}
                                onlyIcon
                                isOutline
                                onClick={toggleModal}
                            />
                        </div>
                    )}

                    <Row className={"justify-content-center position-relative mb-4"}>
                        <Col lg={12} className={"position-relative d-flex justify-content-center"}>
                            <div className={"kpi-doughnut-shape"} style={{ height: "220px" }}>
                                <Doughnut data={chartData} options={options} />
                                <div
                                    className="position-absolute top-20 start-40 translate-middle text-center"
                                    style={{ pointerEvents: "none" }}
                                >
                                    <div style={{ fontSize: "0.8rem", color: "#89898C" }}>Total</div>
                                    <div
                                        style={{
                                            fontSize: "1.2rem",
                                            fontWeight: "bold",
                                            color: "#20ADAF",
                                            lineHeight: "1.2",
                                        }}
                                    >
                                        {formatCurrency(total)}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <div className={"px-2 flex-grow-1"}>
                        {rawStats.map((item, index) => {
                            const dataset = chartData.datasets[0];
                            const bgColor: string = Array.isArray(dataset.backgroundColor)
                                ? dataset.backgroundColor[index]
                                : dataset.backgroundColor;
                            const valueLabel = item.type === "SI" ? "Severidad" : "Frecuencia";

                            return (
                                <div key={Number(index)} className="d-flex align-items-start mb-3">
                                    <div
                                        style={{
                                            backgroundColor: bgColor,
                                            width: "50px",
                                            height: "24px",
                                            borderRadius: "8px",
                                            marginRight: "15px",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <div>
                                        <div
                                            className="fw-bold fst-italic text-dark"
                                            style={{ fontSize: "1rem", lineHeight: "1" }}
                                        >
                                            {typeInquiry === queryChartType.SEVERITY_FREQUENCY ? valueLabel : item.type}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: "0.9rem", marginTop: "2px" }}>
                                            {formatCurrency(item.amount)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardBody>
            )}
        </>
    );

    return (
        <>
            {titleFlag ? (
                <div className="siniestralidad-container-card h-100" style={{ maxWidth: "450px", margin: "auto" }}>
                    <Card
                        className="h-100"
                        style={{
                            borderRadius: "16px",
                            border: "1.5px solid #E0E0E0",
                            boxShadow: "none",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {renderContent()}
                    </Card>
                </div>
            ) : (
                <div className="siniestralidad-container kpi-fixed-height">
                    <div className="header-section-doughnut">
                        <div className="header-title-group">
                            <div className="icon-wrapper">
                                <IconSeveridad />
                            </div>
                            <h2 className="title-label-doughnut">{titleChart}</h2>
                        </div>
                        <ProdesegButton
                            type={ProdesegButtonEnumTypes.PrimaryLight}
                            Icon={IconMenu}
                            onlyIcon
                            isOutline
                            onClick={toggleModal}
                        />
                    </div>
                    <Card className={"flex-grow-1"}>{renderContent()}</Card>
                </div>
            )}

            <StatsModal<CubeSummaryResponse>
                isOpen={isModalOpen}
                toggle={toggleModal}
                title={titleChart}
                data={rawStats}
                labelField="type"
                valueField="amount"
                valueCountField={"total"}
                queryType={typeInquiry}
            />
        </>
    );
};
